import { FC, useState } from "react";
import { Produit } from "../../lib/interfaces/produit.interface";
import { CheckCircle2, Clock3, Download, ExternalLink, FileDown, LockKeyhole, ShoppingCart } from "lucide-react";
import { Button, buttonVariants } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ModeDePaiement } from "../../lib/enums/mode-de-paiement.enum";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ksAchatsQK, ksFacturesAPayerQK, ksFacturesQK } from "../../lib/query-keys";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { useCreateFactureProduit } from "../../lib/hooks/useProduits";
import { useNavigate } from "react-router";
import { useCreateProduitDigitalStripeSession } from "../../lib/hooks/usePaiement";
import { FacturesAPayerResult, FacturesResult } from "../../lib/interfaces/factures.interface";
import { cn } from "@/lib/utils";

interface Props {
  produit: Produit;
  hasFactureEnAttente: boolean;
  isProductBought: boolean;
  isDisabled: boolean;
}

const ProduitItem: FC<Props> = ({ hasFactureEnAttente, isProductBought, produit, isDisabled }) => {
  const [modeDePaiement, setModeDePaiement] = useState<ModeDePaiement | "">("");
  const [termesAcceptes, setTermesAcceptes] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { mutateAsync: createFactureProduit } = useCreateFactureProduit();
  const { mutateAsync: createProduitDigitalStripeSession } = useCreateProduitDigitalStripeSession();

  const redirectToCheckout = async () => {
    const stripeCheckoutSessionId = await createProduitDigitalStripeSession({ productId: produit.id });
    const stripe = await loadStripe(`${import.meta.env.VITE_SNA_STRIPE_KEY}`);
    stripe?.redirectToCheckout({ sessionId: stripeCheckoutSessionId });
  };

  const payerOffline = async () => {
    const facture = await createFactureProduit({
      modeDePaiement: modeDePaiement as ModeDePaiement,
      productId: produit.id,
    });

    queryClient.setQueryData<FacturesResult>([ksFacturesQK], (factures) =>
      factures ? [facture, ...factures.filter((fac) => fac.id !== facture.id)] : [facture]
    );
    queryClient.setQueryData<FacturesAPayerResult>([ksFacturesAPayerQK], (factures) =>
      factures
        ? [
            {
              id: facture.id,
              numero: facture.numero,
              montant: facture.montant,
              description: facture.description,
              date: facture.date,
              sna: facture.sna,
              annotation: facture.annotation ?? null,
              produitId: facture.produitId,
              annulee: false,
            },
            ...factures.filter((fac) => fac.id !== facture.id),
          ]
        : [
            {
              id: facture.id,
              numero: facture.numero,
              montant: facture.montant,
              description: facture.description,
              date: facture.date,
              sna: facture.sna,
              annotation: facture.annotation ?? null,
              produitId: facture.produitId,
              annulee: false,
            },
          ]
    );
    queryClient.invalidateQueries({ queryKey: [ksFacturesQK] });
    queryClient.invalidateQueries({ queryKey: [ksFacturesAPayerQK] });
    navigate(`/boutique/confirmation-achat?id=${facture.id}`);
  };

  const { mutate: handleSubmit, isPending: loading } = useMutation({
    mutationFn: async () => {
      if (modeDePaiement === ModeDePaiement.CB) {
        await redirectToCheckout();
      } else {
        await payerOffline();
      }
    },
    onSuccess: () => {},
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [ksAchatsQK] });
      queryClient.invalidateQueries({ queryKey: [ksFacturesQK] });
      queryClient.invalidateQueries({ queryKey: [ksFacturesAPayerQK] });
    },
    onError: (err: any) => {
      console.error(err.response?.data || err);
      toast.error(`Une erreur s'est produite`);
    },
  });

  const boutonAAfficher = () => {
    // Facture à payer
    if (hasFactureEnAttente) {
      return (
        <div className="flex gap-3 rounded-xl border border-[#ead893] bg-[#fff9e8] p-4 text-sm text-[#725716]">
          <Clock3 className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">Paiement en attente</p>
            <p className="mt-1 leading-5 text-[#806927]">Votre téléchargement sera disponible après règlement.</p>
          </div>
        </div>
      );
    }

    // Produit acheté
    if (isProductBought && produit.digital && produit.url) {
      return (
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#e4efe9] px-3 py-1.5 text-sm font-semibold text-[#173f35]">
            <CheckCircle2 className="h-4 w-4" />
            Ressource achetée
          </div>
          <a
            className={cn(
              buttonVariants({ size: "sm" }),
              "w-full gap-2 bg-[#173f35] hover:bg-[#23594a] sm:w-auto",
            )}
            href={produit.url}
            target="_blank"
            rel="noreferrer"
          >
            <Download className="h-4 w-4" />
            Télécharger
          </a>
        </div>
      );
    }

    if (isDisabled && !isProductBought) {
      return (
        <div className="flex gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
          <LockKeyhole className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
          <div>
            <p className="font-semibold text-slate-800">Accès réservé</p>
            <p className="mt-1 leading-5">
              Un pack actif incluant l'éco-contribution est nécessaire. Régularisez tout paiement en attente pour
              débloquer l'achat.
            </p>
          </div>
        </div>
      );
    }

    // Peut acheter
    return (
      <Dialog
        onOpenChange={() => {
          setTermesAcceptes(false);
          setModeDePaiement("");
        }}
      >
        <DialogTrigger asChild>
          <Button
            className="w-full bg-[#173f35] hover:bg-[#23594a] sm:w-auto"
            icon={<ShoppingCart className="h-4 w-4" />}
          >
            Acheter
          </Button>
        </DialogTrigger>
        <DialogContent className="overflow-hidden p-0 sm:max-w-[560px] sm:rounded-2xl">
          <DialogHeader className="border-b border-slate-100 bg-[#fbfaf6] px-6 py-5">
            <DialogTitle className="text-xl text-slate-950">Acheter {produit.nom}</DialogTitle>
            <DialogDescription>Choisissez votre mode de paiement pour finaliser la commande.</DialogDescription>
          </DialogHeader>
          <form
            className="p-6"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            {/* Radio buttons */}
            <RadioGroup
              disabled={loading}
              value={modeDePaiement}
              onValueChange={(val: ModeDePaiement) => setModeDePaiement(val)}
              className="grid gap-3 sm:grid-cols-3"
            >
              <div className="flex items-center gap-3 rounded-xl border border-slate-200 p-3.5">
                <RadioGroupItem value={ModeDePaiement.CB} id={`cb-${produit.id}`} />
                <Label htmlFor={`cb-${produit.id}`} className="cursor-pointer">Carte bancaire</Label>
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-slate-200 p-3.5">
                <RadioGroupItem value={ModeDePaiement.VIR} id={`vir-${produit.id}`} />
                <Label htmlFor={`vir-${produit.id}`} className="cursor-pointer">Virement</Label>
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-slate-200 p-3.5">
                <RadioGroupItem value={ModeDePaiement.CHQ} id={`chq-${produit.id}`} />
                <Label htmlFor={`chq-${produit.id}`} className="cursor-pointer">Chèque</Label>
              </div>
            </RadioGroup>

            <div className="mt-5 flex items-start gap-3 rounded-xl bg-slate-50 p-4">
              <Checkbox
                id={`terms-${produit.id}`}
                disabled={loading}
                checked={termesAcceptes}
                onCheckedChange={(val: boolean) => setTermesAcceptes(val)}
                className="mt-0.5"
              />
              <label
                htmlFor={`terms-${produit.id}`}
                className="cursor-pointer text-sm leading-5 text-slate-600 peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Pour profiter de mes étiquettes dès réception du paiement, je renonce expressément à mon délai de
                rétractation de 14 jours.
              </label>
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="submit"
                className="w-full bg-[#173f35] hover:bg-[#23594a] sm:w-auto"
                loading={loading}
                disabled={!modeDePaiement || !termesAcceptes}
              >
                Commander et payer {(produit.prix / 100).toFixed(2)} €
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <article className="grid lg:grid-cols-[minmax(280px,0.75fr)_minmax(0,1.25fr)]">
      <div className="flex min-h-64 items-center justify-center bg-[#fbfaf6] p-5 sm:p-7 lg:border-r lg:border-slate-100">
        {produit.imageUrl ? (
          <img
            src={produit.imageUrl}
            alt={produit.nom}
            className="max-h-72 w-full rounded-xl object-contain"
            width={600}
            height={360}
          />
        ) : (
          <div className="flex h-32 w-32 items-center justify-center rounded-full bg-[#f1e8c9] text-[#755c14]">
            <FileDown className="h-12 w-12" />
          </div>
        )}
      </div>

      <div className="flex flex-col p-5 sm:p-7 lg:p-8">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {produit.digital && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#e4efe9] px-2.5 py-1 text-xs font-semibold text-[#173f35]">
              <FileDown className="h-3.5 w-3.5" />
              Téléchargement numérique
            </span>
          )}
        </div>

        <h3 className="text-xl font-bold leading-7 text-slate-950 sm:text-2xl">{produit.nom}</h3>

        <div className="mt-4 space-y-3 text-sm leading-6 text-slate-600">
          <p>
            Afin d'aider le plus grand nombre d'apiculteurs à se mettre en conformité avec la nouvelle réglementation de
            signalétique info-tri, nous proposons à la vente, à nos abonnés à l'Abeille de France et aux adhérents des
            structures départementales affiliées au SNA, 2 types d'étiquettes déjà conçues pour les bocaux en verre et
            les pots en plastique sous format pdf et jpeg.
          </p>
          <p>
            Vous pourrez ainsi facilement intégrer cette nouvelle signalétique à vos étiquettes ou bien les imprimer
            seules et les apposer en complément sur vos pots.
          </p>
          <p>
            Vous trouverez également le Guide de la nouvelle signalétique de tri des emballages ménagers qui détaille
            entre autres les formats minimums de ces étiquettes.
          </p>
          <p>
            Pour plus d'informations concernant la nouvelle signalétique Info-tri à apposer à partir du 9 mars 2023 nous
            vous invitons à consulter l'article correspondant :
            <a
              href="https://www.snapiculture.com/info-tri-citeo-a-appliquer-avant-le-9-mars-2023-emballages-menagers-et-papiers-graphiques/"
              target="_blank"
              rel="noreferrer"
              className="mt-1 flex w-fit items-center gap-1.5 font-semibold text-[#286351] underline decoration-[#d7b64d] decoration-2 underline-offset-4 hover:text-[#173f35]"
            >
              Consignes à appliquer avant le 9 mars 2023
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </p>
        </div>

        <div className="mt-6 border-t border-slate-100 pt-5">
          {!isProductBought && (
            <div className="mb-4 flex items-baseline gap-2">
              <span className="text-sm font-medium text-slate-500">Prix</span>
              <span className="text-2xl font-bold tabular-nums text-slate-950">
                {(produit.prix / 100).toFixed(2)} €
              </span>
            </div>
          )}
          <div>{boutonAAfficher()}</div>
        </div>
      </div>
    </article>
  );
};

export default ProduitItem;
